"use client";

import { c as _c } from "react/compiler-runtime";
import { Card, EllipsisTooltip } from "@dc/ui/components";
import { cn } from "@dc/ui/lib/utils";
import { format } from "date-fns";
import Link from "next/link";
import { useTranslations } from "next-intl";
import type { FC } from "react";
import type { Components } from "react-markdown";
import Markdown from "react-markdown";
import { AlarmLinear, BuildingsLinear, DocumentTextLinear } from "solar-icon-set";
import { NOTIFICATION_CONFIG } from "@/data/notifications/types";
import { useOrgTeamPath } from "@/lib/orgTeamPath";
import { parseCrunchReadyMessage } from "@/lib/parseCrunchReadyMessage";
import { resolveDeepLinkHref } from "@/lib/resolveDeepLinkHref";
import type { NotificationCardProps } from "./NotificationCard.interface";
const markdownComponents: Components = {
  strong: ({
    children
  }) => <strong className="font-semibold">{children}</strong>,
  em: ({
    children
  }) => <u className="font-semibold text-primary underline">{children}</u>
};

/**
 * NotificationCard component displays a notification card with the following properties:
 * @param id - The id of the notification.
 * @param type - The type of the notification.
 * @param title - The title of the notification.
 * @param message - The message of the notification.
 * @param timestamp - The timestamp of the notification.
 * @param isRead - Whether the notification has been read.
 * @param project - The project of the notification.
 * @param dueDate - The due date of the notification.
 * @param link - The link of the notification.
 * @returns
 */
export const NotificationCard: FC<NotificationCardProps> = t0 => {
  const $ = _c(64);
  const {
    id,
    type,
    title,
    message,
    createdAt,
    read,
    notificationContext,
    deepLink,
    onMarkAsRead,
    onNavigate
  } = t0;
  const t = useTranslations("navigation.notifications");
  const {
    build: buildOrgTeamPath
  } = useOrgTeamPath();
  let context;
  try {
    if (typeof notificationContext === "object") {
      context = notificationContext;
    } else {
      let t1;
      if ($[0] !== notificationContext) {
        t1 = JSON.parse(notificationContext);
        $[0] = notificationContext;
        $[1] = t1;
      } else {
        t1 = $[1];
      }
      context = t1;
    }
  } catch {
    context = null;
  }
  const config = NOTIFICATION_CONFIG[type] ?? NOTIFICATION_CONFIG.DOCUMENT;
  const Icon = config.icon;
  const fileName = context && "fileName" in context ? context.fileName as string : null;
  const project = context && "project" in context ? context.project as string : null;
  const dueDate = context && "dueDate" in context ? context.dueDate as string : null;
  let t1;
  if ($[2] !== message || $[3] !== title || $[4] !== type) {
    t1 = parseCrunchReadyMessage(type, title, message);
    $[2] = message;
    $[3] = title;
    $[4] = type;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const crunchReadyMessage = t1;
  let t2;
  if ($[6] !== id || $[7] !== onMarkAsRead || $[8] !== read) {
    t2 = () => {
      if (!read && onMarkAsRead) {
        onMarkAsRead(id);
      }
    };
    $[6] = id;
    $[7] = onMarkAsRead;
    $[8] = read;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const handleMarkAsRead = t2;
  let t3;
  if ($[10] !== config.iconBg || $[11] !== config.iconFg) {
    t3 = cn("flex size-6 items-center justify-center rounded-full text-danger-500", config.iconBg, config.iconFg);
    $[10] = config.iconBg;
    $[11] = config.iconFg;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== Icon) {
    t4 = <Icon size={16} />;
    $[13] = Icon;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== t3 || $[16] !== t4) {
    t5 = <div className={t3}>{t4}</div>;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== title) {
    t6 = <p className="font-mono text-base text-secondary uppercase leading-6">{title}</p>;
    $[18] = title;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== t5 || $[21] !== t6) {
    t7 = <div className="flex items-center gap-1.5">{t5}{t6}</div>;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== read) {
    t8 = !read && <div className="size-2 rounded-full bg-primary" data-testid="unread-indicator" />;
    $[23] = read;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== createdAt) {
    t9 = format(createdAt, "p");
    $[25] = createdAt;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t9) {
    t10 = <div className="flex flex-col items-center justify-center"><p className="text-content3-foreground text-tiny">{t9}</p></div>;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== t10 || $[30] !== t8) {
    t11 = <div className="flex items-center gap-1.5">{t8}{t10}</div>;
    $[29] = t10;
    $[30] = t8;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== t11 || $[33] !== t7) {
    t12 = <div className="flex w-full items-center justify-between">{t7}{t11}</div>;
    $[32] = t11;
    $[33] = t7;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== crunchReadyMessage || $[36] !== message) {
    t13 = <div className="wrap-anywhere text-sm">{crunchReadyMessage ? <><EllipsisTooltip content={crunchReadyMessage.fileName} className="font-semibold text-primary underline" tooltipClassName="wrap-anywhere line-clamp-none" /><p>{crunchReadyMessage.status}</p></> : <Markdown components={markdownComponents}>{message}</Markdown>}</div>;
    $[35] = crunchReadyMessage;
    $[36] = message;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== fileName || $[39] !== project) {
    t14 = fileName ? <div className="flex min-w-0 items-center gap-1.5"><DocumentTextLinear size={16} className="shrink-0 text-default-600" /><EllipsisTooltip as="p" content={fileName} className="text-foreground text-small">{fileName}</EllipsisTooltip></div> : project ? <div className="flex min-w-0 items-center gap-1.5"><BuildingsLinear size={16} className="shrink-0 text-default-600" /><EllipsisTooltip as="p" content={project} className="text-foreground text-small">{project}</EllipsisTooltip></div> : null;
    $[38] = fileName;
    $[39] = project;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== context || $[42] !== dueDate || $[43] !== t) {
    t15 = context && "dueDate" in context && <div className="flex flex-col gap-1.5"><div className="flex items-center gap-1.5"><AlarmLinear size={16} className="text-default-600" /><p className="text-foreground text-tiny"><span className="font-mono uppercase tracking-wide">{t("due")}</span>: {dueDate}</p></div></div>;
    $[41] = context;
    $[42] = dueDate;
    $[43] = t;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== t14 || $[46] !== t15) {
    t16 = <div className="flex w-full items-center justify-between">{t14}{t15}</div>;
    $[45] = t14;
    $[46] = t15;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  let t17;
  if ($[48] !== t13 || $[49] !== t16) {
    t17 = <div className="flex w-full flex-col gap-2.5">{t13}{t16}</div>;
    $[48] = t13;
    $[49] = t16;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  let t18;
  if ($[51] !== t12 || $[52] !== t17) {
    t18 = <div className="flex w-full items-start"><div className="flex min-w-0 flex-1 gap-3"><div className="flex w-full min-w-0 flex-col gap-2.5">{t12}{t17}</div></div></div>;
    $[51] = t12;
    $[52] = t17;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] !== handleMarkAsRead || $[55] !== t18) {
    t19 = <Card className="flex flex-col gap-4 bg-background p-3 shadow-sm hover:bg-foreground-300" onMouseEnter={handleMarkAsRead}>{t18}</Card>;
    $[54] = handleMarkAsRead;
    $[55] = t18;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  const cardContent = t19;
  if (deepLink && deepLink.length > 0) {
    let t20;
    if ($[57] !== buildOrgTeamPath || $[58] !== deepLink[0].url) {
      t20 = resolveDeepLinkHref(deepLink[0].url, buildOrgTeamPath);
      $[57] = buildOrgTeamPath;
      $[58] = deepLink[0].url;
      $[59] = t20;
    } else {
      t20 = $[59];
    }
    const linkUrl = t20;
    let t21;
    if ($[60] !== cardContent || $[61] !== linkUrl || $[62] !== onNavigate) {
      t21 = <Link href={linkUrl} className="block" onClick={onNavigate}>{cardContent}</Link>;
      $[60] = cardContent;
      $[61] = linkUrl;
      $[62] = onNavigate;
      $[63] = t21;
    } else {
      t21 = $[63];
    }
    return t21;
  }
  return cardContent;
};